import { useOrgSettings } from '@/hooks/use-settings';
import { describeZone } from '@/components/settings/time-zone-picker';

/**
 * The ad accounts count their days in another zone than the workspace
 * (api services/adAccountZones, recorded on each spend sync). Every day then
 * sets one clock's orders and visits beside another clock's spend, and no
 * single number looks wrong: Trendia (17 Oct 2026, Chicago ad accounts, a
 * Kolkata workspace) read ROAS 2.37x for days that were 1.48x. A row in the
 * Alerts bell until the zones match.
 */
const PLATFORM = { meta: 'Meta', google: 'Google Ads' };

const hoursApart = (a, b, at) => {
  const min = Math.abs(describeZone(a, at).minutes - describeZone(b, at).minutes);
  const h = min / 60;
  return h ? `${Number.isInteger(h) ? h : h.toFixed(1)} hour${h === 1 ? '' : 's'}` : null;
};

/** Pure: the alert row for `display` (GET /api/settings' display), or null. */
export function zoneAlertOf(display, at = new Date()) {
  const zones = display?.ad_zones;
  const ws = display?.time_zone;
  if (!ws || !Array.isArray(zones) || !zones.length) return null;
  const top = zones[0];
  const who = top.platforms.map((p) => PLATFORM[p] || p).join(' and ');
  const apart = hoursApart(top.zone, ws, at);
  return {
    id: 'zone-mismatch',
    tone: 'warn',
    text: `Your ${who} ad account${top.accounts === 1 ? ' counts its' : 's count their'} days in ${top.zone}, this workspace in ${ws}`,
    to: '/settings?tab=general',
    linkLabel: 'Match the time zone',
    zone: top.zone,
    detail: () => (
      <div className="space-y-2 text-[13.5px] leading-relaxed">
        <p>
          A day here starts at midnight in {ws}; in Ads Manager it starts at midnight in {top.zone}
          {apart ? `, ${apart} apart` : ''}. So each day sets one clock&rsquo;s orders and visits beside another
          clock&rsquo;s spend, and ROAS, cost per order and Arrived against link clicks are off, most on short windows.
        </p>
        <p className="text-muted-foreground">
          In Settings › General, set the time zone to {top.zone} and save. Your daily numbers are rebuilt in it within a few minutes.
        </p>
        {zones.length > 1 && (
          <p className="text-muted-foreground">Other ad accounts use {zones.slice(1).map((z) => z.zone).join(', ')}.</p>
        )}
      </div>
    ),
  };
}

export function useZoneAlerts() {
  const { data } = useOrgSettings();
  const row = zoneAlertOf(data?.display);
  return row ? [row] : [];
}
